<template>
  <!-- Table Container Card -->
  <b-card>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="approvalRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="accountUpgradeRequestList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(dateCreated)="data">
        {{ data.value ? formatDate(data.value) : '' }}
      </template>
      <template #cell(dateReviewed)="data">
        {{ data.value ? formatDate(data.value) : '' }}
      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <feather-icon
          icon="EyeIcon"
          class="cursor-pointer"
          @click="onSelectAccountUpgradeItem(data.item)"
        />
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :title="$t('Account Upgrade Request')"
      :ok-title="$t('Cancel')"
      no-close-on-backdrop
      ok-only
      ok-variant="outline-secondary"
      centered
      size="lg"
    >
      <div class="text-left mb-3">
        <h4 class="mb-3">
          {{ $t('Account Information') }}
        </h4>
        <b-row>
          <b-col
            v-for="(item, index) in upgradeRequestTableItems"
            :key="index"
            md="4"
            class="mb-1"
          >
            <h5 class="font-weight-bolder">
              {{ item.label }}
            </h5>

            <p v-if="item.label !== 'Approval Status'">
              {{ item.text }}
            </p>

            <b-badge
              v-else
              pill
              :variant="getStatusColor(item.text)"
            >
              {{ item.text }}
            </b-badge>
          </b-col>
        </b-row>
      </div>

      <div>
        <div
          v-for="tier in activeTiers"
          :key="tier.title"
        >
          <h4 class="mb-1">
            {{ tier.title }}
          </h4>
          <b-table
            :items="tier.items"
            :per-page="pageSize"
            :current-page="currentPage"
            responsive
            :fields="translatedAccountUpgradeItemHeader"
            primary-key="id"
            show-empty
            :empty-text="$t('No result found')"
            class="position-relative approval-table"
          >
            <template #cell(description)="data">
              {{ addSpaceToString(data.value) }}
            </template>

            <template #cell(previousData)="data">
              <b :class="data.item.previousData !== data.item.newData ? 'text-danger break' : 'break'">
                <Button
                  v-if="isFileField(data.item.description) && data.value"
                  :label="$t('View File')"
                  variant="success"
                  size="md"
                  @click="viewFileImage(data.item.description)"
                />
                <span v-else>{{ data.value }}</span>
              </b>
            </template>

            <template #cell(newData)="data">
              <b :class="data.item.previousData !== data.item.newData ? 'text-success break' : 'break'">
                <Button
                  v-if="isFileField(data.item.description) && data.value"
                  :label="$t('View File')"
                  variant="success"
                  size="md"
                  @click="viewFileImage(data.item.description)"
                />
                <span v-else>{{ data.value }}</span>
              </b>
            </template>
          </b-table>
        </div>
      </div>
      <validation-observer
        v-if="hasBeenApproved"
        ref="accountUpgradeRules"
      >
        <b-form>
          <b-row>
            <b-col md="12">
              <TextAreaField
                id="notes"
                v-model="accountUpgradeFormData.reason"
                :label="$t('Notes')"
                :placeholder="$t('Enter note')"
                :error-name="$t('Note')"
                :rules="isApprovalNoteRequired"
                rows="5"
              />
            </b-col>
          </b-row>
          <div class="d-flex mt-1 mb-3 justify-content-center">
            <Button
              :label="$t('Approve')"
              variant="success"
              :loading="accountButtonStatus.isLoadingApprove"
              :disabled="accountButtonStatus.isDisableApprove"
              size="md"
              @click="handleSubmit(2)"
            />
            <Button
              :label="$t('Decline')"
              :loading="accountButtonStatus.isLoadingReJect"
              :disabled="accountButtonStatus.isDisableReject"
              size="md"
              class="ml-2"
              variant="danger"
              @click="handleSubmit(4)"
            />
          </div>
        </b-form>
      </validation-observer>

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
      <FullScreenSpinner :show="showSpinner" />
    </b-modal>

    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchAccountUpgradesRequests"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Customer ID -->
        <b-col md="6">
          <InputField
            id="customerId"
            v-model="filter.customerID"
            :label="$t('Customer ID')"
            :placeholder="$t('Enter Customer ID')"
            type="text"
          />
        </b-col>
        <!-- Customer ID -->

        <!-- Account Name -->
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="filter.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter Account Name')"
            type="text"
          />
        </b-col>
        <!-- Account Name -->

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="subject-name"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <!-- Email -->
        <b-col md="6">
          <InputField
            id="email-address"
            v-model="filter.email"
            :label="$t('Email')"
            :placeholder="$t('Enter Email')"
            type="email"
          />
        </b-col>
        <!-- Email -->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :placeholder="$t('Select')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Branch -->

        <!-- Product -->
        <b-col md="6">
          <SelectField
            id="product"
            v-model="filter.productCode"
            :label="$t('Product')"
            :placeholder="$t('Select')"
            :options="productOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Product -->

        <!--Date Logged -->
        <b-col md="6">
          <DatePickerField
            id="date-logged"
            v-model="filter.dateCreated"
            :placeholder="$t('Select date')"
            :label="$t('Date Logged')"
            :config="datePickerConfig"
            :disabled="isReadonly"
          />
        </b-col>
        <!-- Date Logged -->

        <!--Date Reviewed -->
        <b-col md="6">
          <DatePickerField
            id="date-reviewed"
            v-model="filter.dateReviewed"
            :placeholder="$t('Select date')"
            :label="$t('Date Reviewed')"
            :config="datePickerConfig"
            :disabled="isReadonly"
          />
        </b-col>
        <!-- Date Reviewed -->

        <!-- Reviewed By -->
        <b-col md="6">
          <SelectField
            id="initiator-name"
            v-model="filter.reviewedBy"
            :label="$t('Reviewed By')"
            :placeholder="$t('Select')"
            :options="usersOptions"
            :reduce="option => option.value"
            item="label"
            :loading="isLoadingInitiatorName"
          />
        </b-col>
        <!-- Reviewed By -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="sanctioned-by"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="translatedStatusOptions"
            :reduce="option => option"
            item="label"
          />
        </b-col>
        <!-- Status -->

      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <!-- View File Image -->
    <b-modal
      id="modal-view-file-image"
      no-close-on-backdrop
      centered
      size="lg"
      :title="viewFileTitle"
      modal-footer
      hide-footer
    >
      <div class="m-2 text-center">
        <b-row>
          <b-col cols="12">
            <b-img
              v-if="activeImageSrc"
              :src="activeImageSrc"
              fluid
              rounded
            />
          </b-col>
        </b-row>
      </div>

      <hr>
      <div class="d-flex align-items-center justify-content-lg-end">
        <Button
          :label="$t('Cancel')"
          size="md"
          class="mr-1"
          variant="outline-secondary"
          @click="$bvModal.hide('modal-view-file-image')"
        />
        <Button
          :label="$t('Download File')"
          size="md"
          icon="DownloadIcon"
          show-icon
          @click="downloadFile"
        />
      </div>
    </b-modal>
    <!-- View File Image -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge, BImg, BForm,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { ValidationObserver } from 'vee-validate'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { eChannelsPermission } from '@/helpers/permissions'
import InputField from '../../../forms/InputField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import SelectField from '../../../forms/SelectField.vue'
import addSpaceToString from '../../../../helpers/addSpaceToString'
import { toYearWithDate, toDateWithYear } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import TextAreaField from '../../../forms/TextAreaField.vue'
import getStatusColor from '../../../../helpers/getStatusColor'

const statusOptions = [
  { label: 'Pending', value: 'Pending' },
  { label: 'Declined', value: 'Declined' },
  { label: 'Approved', value: 'Approved' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BImg,
    BForm,
    ValidationObserver,
    TableEntries,
    Button,
    BTable,
    TablePagination,
    DatePickerField,
    BModal,
    BSpinner,
    FullScreenSpinner,
    InputField,
    SelectField,
    TextAreaField,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert],
  data() {
    return {
      formatDate: toDateWithYear,
      showSpinner: false,
      isLoadingFunctionName: false,
      isLoadingInitiatorName: false,
      accountButtonStatus: {
        isLoadingApprove: false,
        isDisableApprove: false,
        isLoadingReJect: false,
        isDisableReject: false,
      },
      isApprovalNoteRequired: '',
      toYearWithDate,
      getStatusColor,
      isReadonly: false,

      required,
      addSpaceToString,
      hasBeenApproved: false,
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        ariaDateFormat: 'd/m/Y',
      },
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'email', sortable: true, label: 'Email' },
        { key: 'branchName', sortable: true, label: 'Branch Name' },
        { key: 'productName', sortable: true, label: 'Product Name' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'dateReviewed', sortable: true, label: 'Date Reviewed' },
        { key: 'reviewedBy', sortable: true, label: 'Reviewed By' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      accountUpgradeRequestList: [],
      accountUpgradeItemHeader: [
        { key: 'description', sortable: false, label: 'Description' },
        { key: 'previousData', sortable: false, label: 'Previous Data' },
        { key: 'newData', sortable: false, label: 'New Data' },
      ],
      viewFileTitle: '',
      accountUpgradeItems: [],
      accountUpgradeItemsTier2: [],
      accountUpgradeItemsTier3: [],
      selectedImageField: null,
      tierOneRequirement: {},
      tierTwoRequirement: {},
      tierThreeRequirement: {},
      accountTierLevel: '',
      accountTierLevelToUpgradeTo: '',
      accountUpgradeFormData: {
        reason: '',
        accountTierLevel: '',
        id: '',
      },
      filter: {
        customerID: null,
        accountName: null,
        accountNumber: null,
        email: null,
        branchCode: null,
        productCode: null,
        dateCreated: null,
        dateReviewed: null,
        reviewedBy: null,
        status: null,
      },
      usersOptions: [],
      oldData: [],
      newData: [],
      title: '',
      upgradeRequestTableItems: [],
      productOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedAccountUpgradeItemHeader() {
      return this.accountUpgradeItemHeader.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    activeImageSrc() {
      if (!this.selectedImageField) return null

      const allFields = {
        ...this.tierOneRequirement,
        ...this.tierTwoRequirement,
        ...this.tierThreeRequirement,
      }

      return allFields[this.selectedImageField] || null
    },

    activeTiers() {
      const tiers = [
        { title: this.$t('Tier 1 Requirements'), items: this.accountUpgradeItems },
        { title: this.$t('Tier 2 Requirements'), items: this.accountUpgradeItemsTier2 },
        { title: this.$t('Tier 3 Requirements'), items: this.accountUpgradeItemsTier3 },
      ]
      return tiers.slice(0, this.accountTierLevelToUpgradeTo)
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchAccountUpgradesRequests()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchAccountUpgradesRequests()
    },
  },
  mounted() {
    this.fetchAccountUpgradesRequests()
    this.fetchUsers()
    this.fetchCASAProducts()
  },
  methods: {
    ...mapActions({
      getAccountUpgradeRequest: 'eChannels/getAccountUpgradeRequest',
      getAccountUpgradeByAccounNumber: 'eChannels/getAccountUpgradeByAccounNumber',
      getUsers: 'operations/getAllUsers',
      getCASAProduct: 'account/getProducts',
      approveAccountUpgrade: 'eChannels/approveAccountUpgrade',
      declineAccountUpgrade: 'eChannels/declineAccountUpgrade',
      getCustomerInformationByCustomerID: 'account/getCustomerInformationByCustomerID',
    }),
    downloadFile() {
      if (!this.activeImageSrc) return
      const link = document.createElement('a')
      link.href = this.activeImageSrc
      link.download = this.viewFileTitle || 'file'
      link.click()
    },
    async fetchUsers() {
      try {
        this.isLoadingInitiatorName = true
        const formData = {
          status: 'Active',
        }
        const response = await this.getUsers({
          formData,
          permission: eChannelsPermission.VIEW_ACCOUNT_UPGRADE_REQUEST,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.usersOptions = data.data
            // eslint-disable-next-line no-nested-ternary
            .map(item => ({ label: `${item?.firstname ?? ''} ${item?.lastname ?? ''} ${item?.othernames ?? ''}`, value: item.id })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingInitiatorName = false
      }
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getCASAProduct({
          search: { type: 'Savings' },
          page: 0,
          limit: 0,
          permission: eChannelsPermission.VIEW_ACCOUNT_UPGRADE_REQUEST,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchCustomerInformation(customerID) {
      try {
        this.isLoadingInitiatorName = true

        const response = await this.getCustomerInformationByCustomerID({
          customerID,
          permission: eChannelsPermission.VIEW_ACCOUNT_UPGRADE_REQUEST,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          const d = data.data
          this.tierOneRequirement = {
            title: d.title,
            firstName: d.firstName,
            lastName: d.lastName,
            ...(d.bvn && { bvn: d.bvn }),
            ...(d.nin && { nin: d.nin }),
            email: d.email,
            gender: d.gender,
            dateOfBirth: d.dateOfBirth ? toDateWithYear(d.dateOfBirth) : null,
            phoneNumber: d.phoneNumber,
            address: d.address,
            tierOneLivenessCapture: d.passportFileUrl,
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingInitiatorName = false
      }
    },
    isFileField(description) {
      return [
        'tierOneLivenessCapture',
        'tierTwoLivenessCapture',
        'tierThreeLivenessCapture',
        'meansOfIdentificationUrl',
        'utilityBillUrl',
        'signatureUrl',
      ].includes(description)
    },

    viewFileImage(description) {
      this.selectedImageField = description
      const formatted = description
        .replace(/Url$/i, '') // remove "Url" at the end
        .replace(/([A-Z])/g, ' $1') // add space before capitals
        .trim() // remove leading/trailing spaces
        .toLowerCase() // lowercase everything
        .replace(/\b\w/g, c => c.toUpperCase()) // capitalize each word
      this.viewFileTitle = this.$t(`View ${formatted}`)
      this.$root.$emit('bv::show::modal', 'modal-view-file-image')
    },

    async onSelectAccountUpgradeItem(items) {
      try {
        this.showSpinner = true
        const response = await this.getAccountUpgradeByAccounNumber({
          accountNumber: items.accountNumber,
          permission: eChannelsPermission.VIEW_ACCOUNT_UPGRADE_REQUEST,
        })
        await this.fetchCustomerInformation(items.customerID)
        const { status, data } = response
        if (status !== 200 || !data.data) return

        const filteredData = this.filterAccountTierList(data, items)
        if (!filteredData.length) return

        let tier2Item = null
        let tier3Item = null

        filteredData.forEach(item => {
          if (item.accountTierLevelToUpgradeTo === 2) {
            tier2Item = item
          }
          if (item.accountTierLevelToUpgradeTo === 3) {
            tier3Item = item
          }
        })

        const d = (tier2Item && tier3Item) ? tier3Item : (tier2Item || tier3Item)

        if (!d) return

        const currentTierLevel = tier3Item ? tier3Item.accountTierLevel : tier2Item?.accountTierLevel ?? null
        const tierLevelToUpgradeTo = tier3Item
          ? tier3Item.accountTierLevelToUpgradeTo
          : tier2Item?.accountTierLevelToUpgradeTo ?? null

        const isApproved = items.status === 'Approved'
        let isUpgradeTo
        let currentState

        if (isApproved && tierLevelToUpgradeTo === 3) {
          isUpgradeTo = 'All Levels Completed'
          currentState = 3
        } else if (isApproved && tierLevelToUpgradeTo === 2) {
          isUpgradeTo = 'Account Tier Level Upgraded To'
          currentState = 2
        } else {
          isUpgradeTo = 'Account Tier Level To Upgrade To'
          currentState = 1
        }

        this.upgradeRequestTableItems = [
          { label: this.$t('Customer ID'), text: d.customerID },
          { label: this.$t('Account Number'), text: d.accountNumber },
          { label: this.$t('Account Name'), text: d.accountName },
          { label: this.$t('Branch'), text: d.branchName },
          { label: this.$t('Product'), text: d.productName },
          { label: this.$t('Current Tier Level'), text: currentTierLevel },
          {
            label: this.$t(isUpgradeTo),
            text: currentState === 3 ? '' : tierLevelToUpgradeTo,
          },
          // currentState !== 3 && { label: this.$t(isUpgradeTo), text: tierLevelToUpgradeTo },
          // currentState === 3 && { label: this.$t(isUpgradeTo), text: '' },
          { label: this.$t('Account Creation Date'), text: d.accountCreationDate },
        ].filter(Boolean)

        // eslint-disable-next-line no-underscore-dangle
        const status_ = items.status?.toLowerCase()
        this.hasBeenApproved = !['approved', 'declined'].includes(status_)

        this.accountTierLevel = currentTierLevel
        this.accountTierLevelToUpgradeTo = tierLevelToUpgradeTo

        if (tier2Item) {
          this.tierTwoRequirement = {
            bvn: tier2Item.bvn,
            nin: tier2Item.nin,
            tierTwoLivenessCapture: tier2Item.passportUrl,
          }
        }

        if (tier3Item) {
          this.tierThreeRequirement = {
            meansOfIdentification: tier3Item.meansOfIdentification,
            meansOfIdentificationNumber: tier3Item.meansOfIdentificationNumber,
            meansOfIdentificationIssuedDate: tier3Item.meansOfIdentificationIssuedDate
              ? toDateWithYear(tier3Item.meansOfIdentificationIssuedDate)
              : null,
            ...(tier3Item.meansOfIdentificationExpDate && {
              meansOfIdentificationExpDate: toDateWithYear(tier3Item.meansOfIdentificationExpDate),
            }),
            meansOfIdentificationUrl: tier3Item.meansOfIdentificationUrl,
            signatureUrl: tier3Item.signatureUrl,
            utilityBillUrl: tier3Item.utilityBillUrl,
            tierThreeLivenessCapture: tier3Item.passportUrl,
          }
        }

        this.accountUpgradeFormData.id = tier3Item ? tier3Item.id : tier2Item.id
        this.accountUpgradeFormData.accountTierLevel = tierLevelToUpgradeTo

        this.accountUpgradeItems = this.buildUpgradeItems(this.tierOneRequirement)
        this.accountUpgradeItemsTier2 = tier2Item ? this.buildUpgradeItems(this.tierTwoRequirement) : []
        this.accountUpgradeItemsTier3 = tier3Item ? this.buildUpgradeItems(this.tierThreeRequirement) : []

        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },

    filterAccountTierList(response, input) {
      return response.data.filter(record => {
        if (record.accountTierLevelToUpgradeTo === input.accountTierLevelToUpgradeTo) {
          // Exact target tier: match both level and status
          return record.status === input.status
        }
        // Lower tiers: only check that they fall within the upgrade path
        return record.accountTierLevelToUpgradeTo < input.accountTierLevelToUpgradeTo
      })
    },

    buildUpgradeItems(tierData, preApprovedRecord = null) {
      return Object.entries(tierData).map(([key, value]) => {
        const previousData = preApprovedRecord
          ? Object.entries(preApprovedRecord).find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1] ?? ''
          : ''
        return { description: key, newData: value, previousData }
      })
    },
    async handleSubmit(actionType) {
      if (actionType === 2) {
        this.title = this.$t('Approve')
        this.isApprovalNoteRequired = ''
      }
      if (actionType === 4) {
        this.title = this.$t('Decline')
        this.isApprovalNoteRequired = 'required'
      }

      await this.$nextTick()

      this.$refs.accountUpgradeRules.validate().then(success => {
        if (success) {
          const text1 = this.$t('Are you sure you want to')
          const text2 = this.$t('this request ?')
          this.confirm(this.accountUpgradeAction, { text: `${text1} ${this.title} ${text2}` })
        }
      })
    },

    async accountUpgradeAction() {
      try {
        if (this.title === 'Approve') {
          this.accountButtonStatus.isLoadingApprove = true
          this.accountButtonStatus.isDisableApprove = true
        }
        if (this.title === 'Decline') {
          this.accountButtonStatus.isLoadingReJect = true
          this.accountButtonStatus.isDisableReject = true
        }
        const formData = this.accountUpgradeFormData
        const response = this.title === 'Approve'
          ? await this.approveAccountUpgrade({ formData, permission: eChannelsPermission.APPROVE_ACCOUNT_UPGRADE_REQUEST })
          : await this.declineAccountUpgrade({ formData, permission: eChannelsPermission.DECLINE_ACCOUNT_UPGRADE_REQUEST })
        const { status } = response
        if (status === 200) {
          this.accountUpgradeFormData.reason = ''
          this.fetchAccountUpgradesRequests()
          this.hideModal('modal-lg')
          const text1 = this.$t('Your request to')
          const text2 = this.$t('Tier level')
          const text3 = this.$t('was successful')
          this.alertSuccess({ text: `${text1} ${this.title} ${text2} ${this.accountUpgradeFormData.accountTierLevel} ${text3}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.accountButtonStatus.isLoadingReJect = false
        this.accountButtonStatus.isDisableReject = false
        this.accountButtonStatus.isLoadingApprove = false
        this.accountButtonStatus.isDisableApprove = false
      }
    },
    async fetchAccountUpgradesRequests() {
      this.hideModal('modal-filter')
      this.isBusy = true
      try {
        const filter = {
          ...this.filter,
          dateReviewed: this.filter.dateReviewed ? toYearWithDate(this.filter.dateReviewed) : null,
          dateCreated: this.filter.dateCreated ? toYearWithDate(this.filter.dateCreated) : null,
        }
        const response = await this.getAccountUpgradeRequest({
          search: filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: 'VIEW_ACCOUNT_UPGRADE_REQUEST',
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.accountUpgradeRequestList = data.data
          this.totalCount = data.recordCount
        } else {
          this.accountUpgradeRequestList = []
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchAccountUpgradesRequests()
    },
  },
}
</script>
    <style lang="scss" scoped>
    @import '@core/scss/vue/libs/vue-select.scss';
    .approval-table {
      padding-bottom: 65px !important;
    }
    </style>
